import { toast } from "@wootingkb/component-library";
import { MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useCallback, useEffect } from "react";
import {
  ERROR_TOAST_TITLE,
  INFO_TOAST_TITLE,
  LUNAR_NEW_YEAR_TOAST_DESCRIPTION,
  LUNAR_NEW_YEAR_TOAST_TITLE,
  SUCCESS_TOAST_TITLE,
  WARNING_TOAST_TITLE,
} from "./constants/i18n-strings";
import { toast as sonnerToast } from "sonner";

export type ToastText = string | MessageDescriptor;

function isMessageDescriptor(value: unknown): value is MessageDescriptor {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "message" in value
  );
}

type LinguiTranslate = ReturnType<typeof useLingui>["_"];

function resolveToastText(i18n: LinguiTranslate, text: ToastText): string {
  return typeof text === "string" ? text : i18n(text);
}

function resolveToastContent(i18n: LinguiTranslate, content: unknown): string {
  if (isMessageDescriptor(content)) {
    return i18n(content);
  }
  if (content instanceof Error) {
    return content.message;
  }
  return `${content}`;
}

export function useErrorToast() {
  const { _: i18n } = useLingui();

  return useCallback(
    (error: unknown) =>
      toast({
        toastProps: {
          title: i18n(ERROR_TOAST_TITLE),
          description: resolveToastContent(i18n, error),
          showCloseButton: true,
          showIcon: true,
          subcomponents: {
            statusIcon: {
              variant: "error",
            },
          },
        },
        duration: 5000,
      }),
    [i18n]
  );
}

function useWarningToast() {
  const { _: i18n } = useLingui();

  return useCallback(
    (error: unknown) =>
      toast({
        toastProps: {
          title: i18n(WARNING_TOAST_TITLE),
          description: resolveToastContent(i18n, error),
          showCloseButton: true,
          showIcon: true,
          subcomponents: {
            statusIcon: {
              variant: "warning",
            },
          },
        },
        duration: 5000,
      }),
    [i18n]
  );
}

function useInfoToast() {
  const { _: i18n } = useLingui();

  return useCallback(
    (error: unknown) =>
      toast({
        toastProps: {
          title: i18n(INFO_TOAST_TITLE),
          description: resolveToastContent(i18n, error),
          showCloseButton: true,
          showIcon: true,
          subcomponents: {
            statusIcon: {
              variant: "info",
            },
          },
        },
        duration: 5000,
      }),
    [i18n]
  );
}

export function useSuccessToast() {
  const { _: i18n } = useLingui();

  return useCallback(
    (
      description: ToastText,
      options?: {
        title?: ToastText;
        isClosable?: boolean;
        duration?: number;
      }
    ) => {
      const { title, isClosable, duration } = options ?? {};
      return toast({
        toastProps: {
          title: title
            ? resolveToastText(i18n, title)
            : i18n(SUCCESS_TOAST_TITLE),
          description: resolveToastText(i18n, description),
          showCloseButton: isClosable,
          showIcon: true,
          subcomponents: {
            statusIcon: {
              variant: "success",
            },
          },
        },
        duration: duration ?? 5000,
      });
    },
    [i18n]
  );
}

export function useHolidayToast() {
  const { _: i18n } = useLingui();
  useEffect(() => {
    if (Date.now() > new Date("2024-02-17").getTime()) return;
    const toastId = toast({
      toastProps: {
        title: i18n(LUNAR_NEW_YEAR_TOAST_TITLE),
        description: i18n(LUNAR_NEW_YEAR_TOAST_DESCRIPTION),
        showCloseButton: true,
        showIcon: true,
        subcomponents: {
          statusIcon: { variant: "info" },
        },
      },
    });
    return () => {
      sonnerToast.dismiss(toastId);
    };
  }, [i18n]);
}
